/**
 * Claude Desktop ↔ Nexus (Fase 7b, ADR 0020). The desktop app forwards MCP
 * messages as `mcp-request` events; they are answered here with the AI tools
 * and sent back with `mcp_respond`. Changes wait for the user in Nexus.
 */
import { useEffect, useRef } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { handleMcp } from '@nexus/ai';
import type { Store } from '@nexus/store';
import { isTauri } from '@nexus/store/tauri';
import { useStore } from '../db/context';
import { useDescribe } from '../ai/describe';
import { useMcpConfirm } from './state';

export const MCP_SETTING = 'mcp_enabled';

export interface McpInfo {
  running: boolean;
  port: number | null;
  exe: string;
}

export const mcpInfo = () => invoke<McpInfo>('mcp_info');

export async function setMcpEnabled(store: Store, enabled: boolean): Promise<McpInfo> {
  await store.setSetting(MCP_SETTING, String(enabled));
  return invoke<McpInfo>(enabled ? 'mcp_start' : 'mcp_stop');
}

/** A new token: stop and start again (Claude Desktop's bridge reads it each time). */
export async function renewMcpToken(): Promise<McpInfo> {
  await invoke('mcp_stop');
  return invoke<McpInfo>('mcp_start');
}

export function useMcpBridge() {
  const store = useStore();
  const describe = useDescribe();
  const latest = useRef(describe);
  useEffect(() => {
    latest.current = describe;
  });

  useEffect(() => {
    if (!isTauri()) return;
    let off: (() => void) | undefined;
    let disposed = false;
    void (async () => {
      const { listen } = await import('@tauri-apps/api/event');
      const { getVersion } = await import('@tauri-apps/api/app');
      const version = await getVersion().catch(() => '0.0.0');
      const unlisten = await listen<{ id: number; body: string }>('mcp-request', async (e) => {
        let message: unknown = null;
        try {
          message = JSON.parse(e.payload.body);
        } catch {
          /* answered as an invalid request below */
        }
        const reply = await handleMcp(message, {
          store,
          version,
          describe: (p) => latest.current(p),
          confirm: async (preview) => {
            await invoke('mcp_focus').catch(() => {});
            return useMcpConfirm.getState().ask(preview);
          },
        }).catch((err: unknown) => ({
          jsonrpc: '2.0',
          id: null,
          error: { code: -32603, message: String(err) },
        }));
        await invoke('mcp_respond', {
          id: e.payload.id,
          response: reply ? JSON.stringify(reply) : '',
        });
      });
      if (disposed) unlisten();
      else off = unlisten;
      if ((await store.getSetting(MCP_SETTING)) === 'true') await invoke('mcp_start');
    })();
    return () => {
      disposed = true;
      off?.();
    };
  }, [store]);
}
